import { Card, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { type ExternalAccountDetailsAttributes } from "@src/appV2/redesign/Accounts/PayoutSettings/api/schema";
import { ExternalAccountLogo } from "@src/appV2/redesign/Accounts/PayoutSettings/components/ExternalAccountLogo";
import { formatToHoursAndMinutes } from "@src/appV2/redesign/utils/formatToHoursAndMinutes";
import { hoursToSeconds } from "date-fns";

import { type ShiftPaymentStatus } from "../../api/useGetShiftPaymentStatus";
import { EmptyStateText } from "../../components/EmptyStateText";
import { formatUsdFromCents } from "../../utils/formatters";
import { PaymentStatusPill } from "./PaymentStatusPill";
import { PaymentTimingRow } from "./PaymentTimingRow";
import { SectionDivider } from "./SectionDivider";

/**
 * Standard InstantPay fee charged per payout in cents.
 * Using cents avoids float math issues in fee calculations.
 */
const INSTANT_PAY_FEE_CENTS = 299;

interface EarningsCardProps {
  /**
   * Gross earnings in cents from shift API (converted from API dollars at the data layer).
   * Used as fallback when payments API data is unavailable.
   */
  readonly grossEarningsCents: number | undefined;
  /**
   * Hourly rate in cents from shift API (converted from API dollars at the data layer).
   * Used as fallback when payments API data is unavailable.
   */
  readonly hourlyRateCents: number | undefined;
  readonly actualDurationHours: number | undefined;
  readonly isInstantPay: boolean;
  /**
   * Whether the shift is verified (timesheet approved).
   * Used as fallback for payment status when API data is unavailable.
   */
  readonly isVerified: boolean;
  readonly payoutDestination: ExternalAccountDetailsAttributes | undefined;
  /**
   * Payment status from the payments API.
   * When available, amountInCents, includedShiftPayInDollars, and includedShiftTimeInHours
   * are used as the source of truth (matching the Earnings page).
   */
  readonly paymentStatus: ShiftPaymentStatus | undefined;
  /**
   * Whether payment status is currently loading.
   */
  readonly isPaymentStatusLoading: boolean;
}

export function EarningsCard(props: EarningsCardProps) {
  const {
    grossEarningsCents,
    hourlyRateCents,
    actualDurationHours,
    isInstantPay,
    isVerified,
    payoutDestination,
    paymentStatus,
    isPaymentStatusLoading,
  } = props;

  // Use payments API data as source of truth when available (matching Earnings page),
  // falling back to shift API data. Skip negative amounts (reversals).
  const effectiveGrossEarningsCents = paymentStatus?.amountInCents ?? grossEarningsCents;
  const hasEarnings = isDefined(effectiveGrossEarningsCents) && effectiveGrossEarningsCents > 0;

  // Only show fee breakdown for InstantPay shifts with earnings exceeding the fee
  const hasFee = isInstantPay && hasEarnings && effectiveGrossEarningsCents > INSTANT_PAY_FEE_CENTS;

  // Calculate net earnings in cents (integer math - no floating-point issues)
  const netEarningsCents = hasFee ? effectiveGrossEarningsCents - INSTANT_PAY_FEE_CENTS : undefined;

  return (
    <Card variant="tertiary" sx={(theme) => ({ padding: theme.spacing(5) })}>
      <Stack spacing={3}>
        <Text semibold variant="body1">
          Earnings
        </Text>

        {hasEarnings ? (
          <EarningsDetails
            grossEarningsCents={effectiveGrossEarningsCents}
            actualDurationHours={actualDurationHours}
            hourlyRateCents={hourlyRateCents}
            hasFee={hasFee}
            feeCents={INSTANT_PAY_FEE_CENTS}
            netEarningsCents={netEarningsCents}
            paymentStatus={paymentStatus}
            isVerified={isVerified}
            isInstantPay={isInstantPay}
            isPaymentStatusLoading={isPaymentStatusLoading}
            payoutDestination={payoutDestination}
          />
        ) : (
          <EmptyStateText>Earnings information isn&apos;t available yet</EmptyStateText>
        )}
      </Stack>
    </Card>
  );
}

interface EarningsDetailsProps {
  /** Gross earnings in cents. */
  readonly grossEarningsCents: number;
  readonly actualDurationHours: number | undefined;
  /** Hourly rate in cents (converted from API dollars at the data layer). */
  readonly hourlyRateCents: number | undefined;
  readonly hasFee: boolean;
  /** InstantPay fee in cents for display. */
  readonly feeCents: number;
  /** Net earnings in cents (gross - fee), undefined if no fee applies. */
  readonly netEarningsCents: number | undefined;
  readonly paymentStatus: ShiftPaymentStatus | undefined;
  readonly isVerified: boolean;
  readonly isInstantPay: boolean;
  readonly isPaymentStatusLoading: boolean;
  readonly payoutDestination: ExternalAccountDetailsAttributes | undefined;
}

function EarningsDetails(props: EarningsDetailsProps) {
  const {
    grossEarningsCents,
    actualDurationHours,
    hourlyRateCents,
    hasFee,
    feeCents,
    netEarningsCents,
    paymentStatus,
    isVerified,
    isInstantPay,
    isPaymentStatusLoading,
    payoutDestination,
  } = props;

  return (
    <>
      {/* Gross earnings row with worked duration and rate */}
      <EarningsRow
        label={formatGrossLabel(paymentStatus, actualDurationHours, hourlyRateCents)}
        value={formatUsdFromCents(grossEarningsCents)}
      />

      {/* InstantPay fee breakdown (only if there's a fee) */}
      {hasFee && isDefined(netEarningsCents) && (
        <>
          <EarningsRow
            isDeduction
            label="InstantPay fee"
            value={`-${formatUsdFromCents(feeCents)}`}
          />
          <SectionDivider />
          <EarningsRow
            isTotal
            color="primary"
            label="Net earnings"
            value={formatUsdFromCents(netEarningsCents)}
          />
        </>
      )}

      <SectionDivider />

      {/* Payment status - matches Earnings page pill */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
          Status
        </Text>
        <PaymentStatusPill
          paymentStatus={paymentStatus}
          isVerified={isVerified}
          isInstantPay={isInstantPay}
          isLoading={isPaymentStatusLoading}
        />
      </Stack>

      {/* Payment timing - shows date for paid, estimate for pending */}
      <PaymentTimingRow
        paymentStatus={paymentStatus}
        isPaymentStatusLoading={isPaymentStatusLoading}
      />

      {/* Payment destination */}
      {isDefined(payoutDestination) && (
        <>
          <SectionDivider />
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
              Payout destination
            </Text>
            <Stack direction="row" alignItems="center" spacing={2}>
              <ExternalAccountLogo externalAccount={payoutDestination} />
              <Text variant="body2">
                {payoutDestination.providerName ?? "Account"} ···· {payoutDestination.last4}
              </Text>
            </Stack>
          </Stack>
        </>
      )}
    </>
  );
}

interface EarningsRowProps {
  readonly label: string;
  readonly value: string;
  readonly isDeduction?: boolean;
  readonly isTotal?: boolean;
  readonly color?: "primary" | "secondary";
}

function EarningsRow(props: EarningsRowProps) {
  const { label, value, isDeduction = false, isTotal = false, color = "secondary" } = props;

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Text
        semibold={isTotal}
        variant="body2"
        sx={(theme) => ({
          color: color === "primary" ? theme.palette.text.primary : theme.palette.text.secondary,
        })}
      >
        {label}
      </Text>
      <Text
        semibold={isTotal}
        variant="body2"
        sx={(theme) => ({
          color: getValueColor(theme, isDeduction),
        })}
      >
        {value}
      </Text>
    </Stack>
  );
}

function getValueColor(
  theme: { palette: { text: { secondary: string; primary: string } } },
  isDeduction: boolean
): string {
  if (isDeduction) {
    return theme.palette.text.secondary;
  }
  return theme.palette.text.primary;
}

/**
 * Formats the gross earnings label with duration and rate.
 * Uses payments API included shift data as source of truth (matching Earnings page),
 * falling back to shift API data.
 */
function formatGrossLabel(
  paymentStatus: ShiftPaymentStatus | undefined,
  fallbackDurationHours: number | undefined,
  fallbackHourlyRateCents: number | undefined
): string {
  // Use payments API included shift data when available (matching Earnings page)
  const timeInHours = paymentStatus?.includedShiftTimeInHours ?? fallbackDurationHours;
  const payInDollars = paymentStatus?.includedShiftPayInDollars;

  if (isDefined(timeInHours) && isDefined(payInDollars)) {
    // Format matching Earnings page: formatToHoursAndMinutes(hoursToSeconds(time))
    const formattedDuration = formatToHoursAndMinutes(hoursToSeconds(timeInHours));
    const formattedRate = formatDollarsAsUsd(payInDollars);
    return `Gross earnings (${formattedDuration} @ ${formattedRate}/hr)`;
  }

  // Fallback to shift API data in cents
  if (isDefined(timeInHours) && isDefined(fallbackHourlyRateCents)) {
    const formattedDuration = formatToHoursAndMinutes(hoursToSeconds(timeInHours));
    const formattedRate = formatUsdFromCents(fallbackHourlyRateCents);
    return `Gross earnings (${formattedDuration} @ ${formattedRate}/hr)`;
  }

  return "Gross earnings";
}
